/**
 * Build — the geometry kitchen for the character mesh.
 *
 * Everything a soldier is made of is lofted here: capped, bevelled cross-sections
 * swept along a path, superellipsoid "rounded boxes" for hard gear, and ribbon
 * straps that follow an arbitrary polyline. One `SkinBuilder` accumulates the whole
 * character into a single indexed BufferGeometry with four material groups, so a
 * finished soldier is one THREE.SkinnedMesh and four draw calls.
 *
 * UVs are emitted in METRES (1 uv unit = 1 m of surface). The materials team's
 * `worldSize: 1` option then resolves to physically-uniform texel density on every
 * part with no per-part tuning, and per-variant camo offsets are a uv offset. Each
 * part additionally carries a seeded uv rotation/offset (`setUV`) so that physically
 * uniform density does not also mean the tile lines up between chest, sleeve and thigh.
 *
 * `build()` bakes cavity ambient occlusion into the vertex colour before returning —
 * see `bakeCavityAO`. There is no second uv set to hold an AO map, and a character
 * with no occlusion under the helmet brim, the chin and the collar reads as plastic.
 *
 * owner: characters
 */
import * as THREE from 'three';

/** material slots — index into the SkinnedMesh material array */
export const SLOT = { SKIN: 0, CLOTH: 1, WEBBING: 2, HARD: 3 };
export const SLOT_COUNT = 4;

const _a = new THREE.Vector3();
const _b = new THREE.Vector3();
const _c = new THREE.Vector3();
const _d = new THREE.Vector3();
const _q = new THREE.Quaternion();

/** Superellipse unit outline. `round` 1 = circle, → 0.12 = square with a fat bevel. */
function superXY(theta, round, out) {
  const c = Math.cos(theta), s = Math.sin(theta);
  const k = round === undefined ? 1 : (round < 0.12 ? 0.12 : round > 1 ? 1 : round);
  if (k === 1) { out.x = c; out.y = s; return out; }
  out.x = Math.sign(c) * Math.pow(Math.abs(c), k);
  out.y = Math.sign(s) * Math.pow(Math.abs(s), k);
  return out;
}

/** Normalise a weight list to at most 4 bones summing to 1. */
export function normWeights(list) {
  const l = list.filter((e) => e[1] > 1e-4).sort((x, y) => y[1] - x[1]).slice(0, 4);
  let s = 0;
  for (const e of l) s += e[1];
  if (s <= 0) return [[0, 1]];
  for (const e of l) e[1] /= s;
  return l;
}

/** Blend two weight lists. */
export function mixWeights(a, b, t) {
  const m = new Map();
  for (const [i, w] of a) m.set(i, (m.get(i) || 0) + w * (1 - t));
  for (const [i, w] of b) m.set(i, (m.get(i) || 0) + w * t);
  return normWeights([...m.entries()]);
}

export class SkinBuilder {
  constructor(rig) {
    this.rig = rig;
    this.pos = [];
    this.uv = [];
    this.col = [];
    this.si = [];
    this.sw = [];
    this.idx = [[], [], [], []];   // one index array per material slot
    this.slot = SLOT.CLOTH;
    this.tint = [1, 1, 1];
    this._seams = [];              // vertex pairs that must share a normal
    // per-part uv transform — see setUV()
    this._uvs = 1; this._uvc = 1; this._uvsn = 0; this._uvox = 0; this._uvoy = 0;
  }

  setSlot(s) { this.slot = s; return this; }
  setTint(hex) {
    const c = new THREE.Color(hex);
    this.tint = [c.r, c.g, c.b];
    return this;
  }

  /**
   * Per-part uv transform.
   *
   * UVs are authored in metres, which gives every part physically-correct texel
   * density but also lines the tile up perfectly across the torso, the sleeve and
   * the thigh — so the eye finds the repeat in one glance. Rotating and offsetting
   * each part by a seeded amount keeps the density and destroys the alignment, and
   * `scale` lets a genuinely different weave (a helmet cover, a rubber sole) run at
   * its own pitch on the same material.
   */
  setUV(scale = 1, ox = 0, oy = 0, rot = 0) {
    this._uvs = scale;
    this._uvc = Math.cos(rot);
    this._uvsn = Math.sin(rot);
    this._uvox = ox;
    this._uvoy = oy;
    return this;
  }

  get vertexCount() { return this.pos.length / 3; }

  /** `tint` overrides the builder tint for this one vertex (used by the face). */
  pushVertex(p, uvx, uvy, weights, tint) {
    this.pos.push(p.x, p.y, p.z);
    const s = this._uvs, c = this._uvc, sn = this._uvsn;
    this.uv.push((uvx * c - uvy * sn) * s + this._uvox, (uvx * sn + uvy * c) * s + this._uvoy);
    const t = tint || this.tint;
    this.col.push(t[0], t[1], t[2]);
    const w = weights;
    for (let i = 0; i < 4; i++) {
      this.si.push(w[i] ? w[i][0] : 0);
      this.sw.push(w[i] ? w[i][1] : 0);
    }
    return this.vertexCount - 1;
  }

  tri(a, b, c) { this.idx[this.slot].push(a, b, c); }
  quad(a, b, c, d) { const t = this.idx[this.slot]; t.push(a, b, c, a, c, d); }

  /**
   * Sweep a closed cross-section along a list of rings.
   *
   * ring = { p:Vector3, x:Vector3, y:Vector3, rx, ry, round, w:[[bone,weight]], twist? }
   * `x`/`y` span the section plane; the sweep direction is x × y.
   *
   * opts: { sides, capStart, capEnd, uOffset, vOffset, uScale }
   */
  loft(rings, opts = {}) {
    const sides = opts.sides ?? 14;
    const n = rings.length;
    if (n < 2) return null;

    // arc length along the path for V
    const vs = new Array(n);
    vs[0] = opts.vOffset ?? 0;
    for (let i = 1; i < n; i++) vs[i] = vs[i - 1] + rings[i].p.distanceTo(rings[i - 1].p);

    const grid = [];
    const sxy = new THREE.Vector2();
    for (let i = 0; i < n; i++) {
      const r = rings[i];
      const row = [];
      // perimeter estimate for uniform U density
      const per = Math.PI * (r.rx + r.ry) * (opts.uScale ?? 1);
      const w = normWeights(r.w.map((e) => e.slice()));
      for (let j = 0; j < sides; j++) {
        const th = (j / sides) * Math.PI * 2 + (r.twist || 0);
        superXY(th, r.round ?? 1, sxy);
        _a.copy(r.p)
          .addScaledVector(r.x, sxy.x * r.rx)
          .addScaledVector(r.y, sxy.y * r.ry);
        row.push(this.pushVertex(_a, (j / sides) * per + (opts.uOffset ?? 0), vs[i], w));
      }
      // Seam vertex: same position as j=0 but u = perimeter, so the last quad does not
      // run the texture backwards across the whole tile. Normals get welded in build().
      superXY(r.twist || 0, r.round ?? 1, sxy);
      _a.copy(r.p).addScaledVector(r.x, sxy.x * r.rx).addScaledVector(r.y, sxy.y * r.ry);
      const seam = this.pushVertex(_a, per + (opts.uOffset ?? 0), vs[i], w);
      this._seams.push(row[0], seam);
      row.push(seam);
      grid.push(row);
    }

    for (let i = 0; i < n - 1; i++) {
      for (let j = 0; j < sides; j++) {
        this.quad(grid[i][j], grid[i][j + 1], grid[i + 1][j + 1], grid[i + 1][j]);
      }
    }

    if (opts.capStart !== false) this._cap(rings[0], sides, true, opts);
    if (opts.capEnd !== false) this._cap(rings[n - 1], sides, false, opts);
    return grid;
  }

  /** Flat, hard-edged cap: duplicated ring so the crease survives smoothing. */
  _cap(ring, sides, flip, opts) {
    const w = normWeights(ring.w.map((e) => e.slice()));
    const centre = this.pushVertex(ring.p, 0, 0, w);
    const ids = [];
    const sxy = new THREE.Vector2();
    for (let j = 0; j < sides; j++) {
      const th = (j / sides) * Math.PI * 2 + (ring.twist || 0);
      superXY(th, ring.round ?? 1, sxy);
      _a.copy(ring.p).addScaledVector(ring.x, sxy.x * ring.rx).addScaledVector(ring.y, sxy.y * ring.ry);
      ids.push(this.pushVertex(_a, sxy.x * ring.rx, sxy.y * ring.ry, w));
    }
    for (let j = 0; j < sides; j++) {
      const a = ids[j], b = ids[(j + 1) % sides];
      if (flip) this.tri(centre, b, a); else this.tri(centre, a, b);
    }
  }

  /**
   * Rounded/bevelled box built as a superellipsoid — every edge catches a highlight,
   * which is the single loudest amateur tell if you skip it.
   *
   * opts: { centre, quat, half:[x,y,z], round (0..1), segU, segV, w }
   */
  superBox(opts) {
    const half = opts.half;
    const round = opts.round ?? 0.28;
    const segU = opts.segU ?? 14;   // around
    const segV = opts.segV ?? 8;    // pole to pole
    const q = opts.quat || _q.identity();
    const c = opts.centre;
    const w = normWeights(opts.w.map((e) => e.slice()));
    const kk = Math.max(0.10, Math.min(1, round));

    const spow = (v, p) => Math.sign(v) * Math.pow(Math.abs(v), p);
    const uSpan = (half[0] + half[2]) * 2;
    const vSpan = half[1] * 2;
    const grid = [];
    // Rows 0 and segV are geometric POLES: every sample collapses to one point, so
    // emitting a full ring there produced `segU` zero-area triangles per box and ~1200
    // per soldier. Zero-area triangles render nothing but they poison
    // `computeVertexNormals` and `computeTangents` (NaN), so they are collapsed here.
    const pole = (sign) => {
      _a.set(0, sign * half[1], 0).applyQuaternion(q).add(c);
      return this.pushVertex(_a, uSpan * 0.5, sign < 0 ? 0 : vSpan, w);
    };
    const pLo = pole(-1);
    for (let i = 1; i < segV; i++) {
      const v = (i / segV) * Math.PI - Math.PI / 2;
      const cv = Math.cos(v), sv = Math.sin(v);
      const row = [];
      for (let j = 0; j <= segU; j++) {
        const u = (j / segU) * Math.PI * 2;
        const cu = Math.cos(u), su = Math.sin(u);
        _a.set(spow(cv, kk) * spow(cu, kk) * half[0],
               spow(sv, kk) * half[1],
               spow(cv, kk) * spow(su, kk) * half[2]);
        _a.applyQuaternion(q).add(c);
        const id = this.pushVertex(_a, (j / segU) * uSpan, (i / segV) * vSpan, w);
        if (j === segU) this._seams.push(row[0], id);
        row.push(id);
      }
      grid.push(row);
    }
    const pHi = pole(1);
    for (let i = 0; i < grid.length - 1; i++) {
      for (let j = 0; j < segU; j++) {
        this.quad(grid[i][j], grid[i][j + 1], grid[i + 1][j + 1], grid[i + 1][j]);
      }
    }
    const first = grid[0], last = grid[grid.length - 1];
    for (let j = 0; j < segU; j++) {
      this.tri(pLo, first[j + 1], first[j]);
      this.tri(pHi, last[j], last[j + 1]);
    }
    return grid;
  }

  /**
   * A strap / ribbon that follows a polyline. Parallel-transport frames keep it from
   * twisting, and the `up` hint keeps the flat face against the body.
   *
   * opts: { width, thick, round, sides, w, wAt }
   */
  strap(points, opts = {}) {
    const width = opts.width ?? 0.045;
    const thick = opts.thick ?? 0.012;
    const sides = opts.sides ?? 8;
    const rings = [];
    const n = points.length;
    let prevUp = null;
    for (let i = 0; i < n; i++) {
      const p = points[i];
      _a.copy(points[Math.min(n - 1, i + 1)]).sub(points[Math.max(0, i - 1)]);
      if (_a.lengthSq() < 1e-9) _a.set(0, 1, 0);
      _a.normalize();                                   // tangent
      // the "up" of the ribbon = outward from the body axis
      const up = (opts.up ? opts.up(p, i) : _b.set(p.x, 0, p.z).normalize()).clone();
      if (up.lengthSq() < 1e-6) up.set(0, 0, 1);
      if (prevUp && up.dot(prevUp) < 0) up.negate();
      prevUp = up;
      const right = _c.crossVectors(_a, up).normalize().clone();
      const realUp = _d.crossVectors(right, _a).normalize().clone();
      rings.push({
        p: p.clone(), x: right, y: realUp,
        rx: width * 0.5, ry: thick * 0.5, round: opts.round ?? 0.42,
        w: opts.wAt ? opts.wAt(p) : opts.w,
      });
    }
    return this.loft(rings, { sides, capStart: opts.capStart, capEnd: opts.capEnd });
  }

  /** Finished indexed geometry with one group per material slot. */
  build() {
    const idx = [];
    const groups = [];
    for (let s = 0; s < SLOT_COUNT; s++) {
      const start = idx.length;
      for (const v of this.idx[s]) idx.push(v);
      groups.push({ start, count: idx.length - start, materialIndex: s });
    }

    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(this.pos, 3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute(this.uv, 2));
    g.setAttribute('color', new THREE.Float32BufferAttribute(this.col, 3));
    g.setAttribute('skinIndex', new THREE.Uint16BufferAttribute(this.si, 4));
    g.setAttribute('skinWeight', new THREE.Float32BufferAttribute(this.sw, 4));
    g.setIndex(this.vertexCount > 65535
      ? new THREE.Uint32BufferAttribute(idx, 1)
      : new THREE.Uint16BufferAttribute(idx, 1));
    for (const gr of groups) if (gr.count > 0) g.addGroup(gr.start, gr.count, gr.materialIndex);

    g.computeVertexNormals();

    // Weld the normals of uv seam duplicates. Without this every loft has a visible
    // hard crease running down one side where the uv wrapped.
    const nrm = g.attributes.normal.array;
    for (let i = 0; i < this._seams.length; i += 2) {
      const a = this._seams[i] * 3, b = this._seams[i + 1] * 3;
      let x = nrm[a] + nrm[b], y = nrm[a + 1] + nrm[b + 1], z = nrm[a + 2] + nrm[b + 2];
      const l = Math.hypot(x, y, z) || 1;
      x /= l; y /= l; z /= l;
      nrm[a] = x; nrm[a + 1] = y; nrm[a + 2] = z;
      nrm[b] = x; nrm[b + 1] = y; nrm[b + 2] = z;
    }
    g.attributes.normal.needsUpdate = true;

    if (this.aoStrength !== 0) bakeCavityAO(g, this.aoStrength ?? 0.58);

    // Tangents: the materials team builds its TBN from screen-space derivatives, so
    // this is belt-and-braces for anything that wants a real tangent frame. Degenerate
    // uv triangles (loft caps) can produce NaN, which would blow up a normal map.
    try {
      g.computeTangents();
      const t = g.attributes.tangent.array;
      for (let i = 0; i < t.length; i += 4) {
        if (!Number.isFinite(t[i]) || !Number.isFinite(t[i + 1]) || !Number.isFinite(t[i + 2])
            || (t[i] === 0 && t[i + 1] === 0 && t[i + 2] === 0)) {
          t[i] = 1; t[i + 1] = 0; t[i + 2] = 0; t[i + 3] = 1;
        }
      }
    } catch (e) { g.deleteAttribute('tangent'); }

    g.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0.95, 0), 1.9);
    g.boundingBox = new THREE.Box3(new THREE.Vector3(-1.2, -0.4, -1.2), new THREE.Vector3(1.2, 2.2, 1.2));
    return g;
  }
}

/* ------------------------------------------------------------------ *
 * cavity ambient occlusion — baked into the vertex colour
 * ------------------------------------------------------------------ */

/**
 * 24 directions on the +Y hemisphere, cosine-ish distributed (Fibonacci lattice).
 * Built once; rotated into each vertex's tangent frame at bake time.
 */
const AO_DIRS = (() => {
  const n = 28, out = [];
  const ga = Math.PI * (3 - Math.sqrt(5));
  for (let i = 0; i < n; i++) {
    // cosine-weighted: y = sqrt(1 - u) packs samples toward the normal. A uniform
    // hemisphere puts most of its rays near the horizon, where a coarse grid always
    // reports a hit on the vertex's OWN surface, and every flat panel came out at 0.47.
    const u = (i + 0.5) / n;
    const y = Math.sqrt(1 - u);
    const r = Math.sqrt(Math.max(0, 1 - y * y));
    const th = ga * i;
    out.push([Math.cos(th) * r, y, Math.sin(th) * r]);
  }
  return out;
})();
const AO_STEPS = [0.028, 0.055, 0.095, 0.150];

/**
 * Bake cavity occlusion into `geometry.attributes.color`.
 *
 * A character with no cavity AO reads as plastic faster than any other single
 * defect: the underside of a helmet brim, the shadow under a chin and the inside of
 * a collar all come out at exactly the value of fully-exposed skin. There is no uv
 * space here to hold an AO *map* — the whole mesh is one metric-uv atlas shared with
 * the material library — so the occlusion goes into the vertex colour, which the
 * shared materials already multiply by (`vertexColors: true`).
 *
 * The visibility query is a coarse occupancy grid rather than a ray/triangle sweep:
 * 14 k triangles against 9 k vertices is 130 M intersection tests, a 12 mm grid is
 * two array lookups per sample and lands within a few percent of the analytic answer
 * at this scale.
 */
export function bakeCavityAO(geometry, strength = 0.62) {
  const pos = geometry.attributes.position.array;
  const nrm = geometry.attributes.normal.array;
  const col = geometry.attributes.color;
  const idx = geometry.index.array;
  const nv = pos.length / 3;
  if (!col || !nv) return;

  /* ---- occupancy grid ---- */
  const CELL = 0.013;
  let x0 = Infinity, y0 = Infinity, z0 = Infinity, x1 = -Infinity, y1 = -Infinity, z1 = -Infinity;
  for (let i = 0; i < nv; i++) {
    const x = pos[i * 3], y = pos[i * 3 + 1], z = pos[i * 3 + 2];
    if (x < x0) x0 = x; if (x > x1) x1 = x;
    if (y < y0) y0 = y; if (y > y1) y1 = y;
    if (z < z0) z0 = z; if (z > z1) z1 = z;
  }
  const pad = CELL * 2;
  x0 -= pad; y0 -= pad; z0 -= pad; x1 += pad; y1 += pad; z1 += pad;
  const nx = Math.max(1, Math.ceil((x1 - x0) / CELL));
  const ny = Math.max(1, Math.ceil((y1 - y0) / CELL));
  const nz = Math.max(1, Math.ceil((z1 - z0) / CELL));
  if (nx * ny * nz > 4_000_000) return;
  const grid = new Uint8Array(nx * ny * nz);
  const at = (ix, iy, iz) => grid[(iy * nz + iz) * nx + ix];
  const mark = (x, y, z) => {
    const ix = ((x - x0) / CELL) | 0, iy = ((y - y0) / CELL) | 0, iz = ((z - z0) / CELL) | 0;
    if (ix < 0 || iy < 0 || iz < 0 || ix >= nx || iy >= ny || iz >= nz) return;
    grid[(iy * nz + iz) * nx + ix] = 1;
  };

  // Rasterise every triangle by barycentric sampling at roughly the cell pitch, so a
  // large flat panel does not leave holes the way vertex-only marking would.
  for (let t = 0; t < idx.length; t += 3) {
    const a = idx[t] * 3, b = idx[t + 1] * 3, c = idx[t + 2] * 3;
    const ax = pos[a], ay = pos[a + 1], az = pos[a + 2];
    const bx = pos[b], by = pos[b + 1], bz = pos[b + 2];
    const cx = pos[c], cy = pos[c + 1], cz = pos[c + 2];
    const e0 = Math.hypot(bx - ax, by - ay, bz - az);
    const e1 = Math.hypot(cx - ax, cy - ay, cz - az);
    const n = Math.min(6, Math.max(1, Math.ceil(Math.max(e0, e1) / CELL)));
    for (let i = 0; i <= n; i++) {
      for (let j = 0; i + j <= n; j++) {
        const u = i / n, v = j / n, w = 1 - u - v;
        mark(ax * w + bx * u + cx * v, ay * w + by * u + cy * v, az * w + bz * u + cz * v);
      }
    }
  }

  /* ---- sample ---- */
  const ao = new Float32Array(nv);
  let tx = 0, ty = 0, tz = 0, sx = 0, sy = 0, sz = 0;
  for (let i = 0; i < nv; i++) {
    const px = pos[i * 3], py = pos[i * 3 + 1], pz = pos[i * 3 + 2];
    let nxv = nrm[i * 3], nyv = nrm[i * 3 + 1], nzv = nrm[i * 3 + 2];
    const nl = Math.hypot(nxv, nyv, nzv) || 1;
    nxv /= nl; nyv /= nl; nzv /= nl;
    // tangent frame with +Y along the normal
    if (Math.abs(nyv) < 0.9) { tx = 0; ty = 1; tz = 0; } else { tx = 1; ty = 0; tz = 0; }
    sx = ty * nzv - tz * nyv; sy = tz * nxv - tx * nzv; sz = tx * nyv - ty * nxv;
    const sl = Math.hypot(sx, sy, sz) || 1;
    sx /= sl; sy /= sl; sz /= sl;
    tx = nyv * sz - nzv * sy; ty = nzv * sx - nxv * sz; tz = nxv * sy - nyv * sx;

    // start just off the surface so a vertex never occludes itself
    const ox = px + nxv * 0.018, oy = py + nyv * 0.018, oz = pz + nzv * 0.018;
    let occ = 0, wsum = 0;
    for (let d = 0; d < AO_DIRS.length; d++) {
      const D = AO_DIRS[d];
      const dx = sx * D[0] + nxv * D[1] + tx * D[2];
      const dy = sy * D[0] + nyv * D[1] + ty * D[2];
      const dz = sz * D[0] + nzv * D[1] + tz * D[2];
      const cw = D[1];                      // cosine weight
      wsum += cw;
      for (let s = 0; s < AO_STEPS.length; s++) {
        const L = AO_STEPS[s];
        const ix = ((ox + dx * L - x0) / CELL) | 0;
        const iy = ((oy + dy * L - y0) / CELL) | 0;
        const iz = ((oz + dz * L - z0) / CELL) | 0;
        if (ix < 0 || iy < 0 || iz < 0 || ix >= nx || iy >= ny || iz >= nz) break;
        if (at(ix, iy, iz)) { occ += cw * (1 - s / AO_STEPS.length); break; }
      }
    }
    ao[i] = 1 - strength * (occ / (wsum || 1));
  }

  // Normalise so the most-exposed decile lands exactly on 1.0. Without this the whole
  // character darkens by the mean occlusion and every downstream albedo gain has to be
  // re-tuned; with it, AO only ever darkens a cavity relative to open surface, which is
  // the only thing it is supposed to do.
  {
    const sorted = Float32Array.from(ao).sort();
    const ref = sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.90))] || 1;
    if (ref > 0.05) for (let i = 0; i < nv; i++) ao[i] = Math.min(1, ao[i] / ref);
  }

  // One smoothing pass over the index topology — the grid is coarse enough that a
  // single vertex can catch a cell its neighbours miss, and that reads as a speckle.
  const acc = new Float32Array(nv);
  const cnt = new Uint16Array(nv);
  for (let t = 0; t < idx.length; t += 3) {
    const a = idx[t], b = idx[t + 1], c = idx[t + 2];
    acc[a] += ao[b] + ao[c]; cnt[a] += 2;
    acc[b] += ao[a] + ao[c]; cnt[b] += 2;
    acc[c] += ao[a] + ao[b]; cnt[c] += 2;
  }
  const arr = col.array;
  for (let i = 0; i < nv; i++) {
    const v = cnt[i] ? (ao[i] * 0.45 + (acc[i] / cnt[i]) * 0.55) : ao[i];
    const k = v < 0.32 ? 0.32 : v > 1 ? 1 : v;
    arr[i * 3] *= k; arr[i * 3 + 1] *= k; arr[i * 3 + 2] *= k;
  }
  col.needsUpdate = true;
}

/* ------------------------------------------------------------------ *
 * path helpers
 * ------------------------------------------------------------------ */

/** Catmull-Rom sample of a control polyline, t in [0,1]. */
export function crSample(pts, t, out = new THREE.Vector3()) {
  const n = pts.length;
  const f = t * (n - 1);
  let i = Math.floor(f);
  if (i > n - 2) i = n - 2;
  if (i < 0) i = 0;
  const u = f - i;
  const p0 = pts[Math.max(0, i - 1)], p1 = pts[i], p2 = pts[i + 1], p3 = pts[Math.min(n - 1, i + 2)];
  const u2 = u * u, u3 = u2 * u;
  out.set(
    0.5 * ((2 * p1.x) + (-p0.x + p2.x) * u + (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * u2 + (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * u3),
    0.5 * ((2 * p1.y) + (-p0.y + p2.y) * u + (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * u2 + (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * u3),
    0.5 * ((2 * p1.z) + (-p0.z + p2.z) * u + (2 * p0.z - 5 * p1.z + 4 * p2.z - p3.z) * u2 + (-p0.z + 3 * p1.z - 3 * p2.z + p3.z) * u3),
  );
  return out;
}

export function crTangent(pts, t, out = new THREE.Vector3()) {
  const h = 1e-3;
  const a = crSample(pts, Math.max(0, t - h), new THREE.Vector3());
  const b = crSample(pts, Math.min(1, t + h), new THREE.Vector3());
  return out.copy(b).sub(a).normalize();
}

export const smooth = (e0, e1, x) => {
  const t = Math.max(0, Math.min(1, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
};
export const lerp = (a, b, t) => a + (b - a) * t;
export const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);
